/**
 * The feed: every item in the current view, NEWEST FIRST. A dig fills a room
 * faster than anyone reads it, so what just landed is the only part worth
 * putting where the eye already is; the history is below, and "load more"
 * walks further back.
 *
 * The DATA stays oldest-first — `upsertItem` keeps ascending id order and the
 * server pages by `after_id` — so only the render is reversed. Turning the
 * list itself around would put the paging cursor at the wrong end.
 */
import React, { useEffect, useRef } from 'react';
import type { BoardroomItem, BoardroomPersona } from '../../types';
import ItemCard from './ItemCard';

export default function ItemFeed({
  items,
  personas,
  hasMore,
  loading,
  onLoadMore,
  onOpen,
  emptyText,
}: {
  items: BoardroomItem[];
  personas: Map<number, BoardroomPersona>;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onOpen: (item: BoardroomItem) => void;
  emptyText: React.ReactNode;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // Ascending data, so the last row is the newest.
  const newestId = items.length ? items[items.length - 1].id : 0;
  // Newest first on screen. `slice` because `reverse` mutates, and the array
  // handed in is the caller's state.
  const shown = items.slice().reverse();

  // Follow the feed while you are near the top; leave you alone otherwise —
  // scrolling back through the history should not be yanked away every time a
  // persona records something.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollTop < 160) el.scrollTo({ top: 0 });
  }, [newestId]);

  return (
    <div ref={scrollerRef} className="h-full min-h-0 overflow-y-auto pr-1">
      {items.length === 0 && !loading ? (
        <div className="text-center py-16 text-sm text-gray-500 dark:text-gray-400">{emptyText}</div>
      ) : (
        <div className="flex flex-col gap-2">
          {shown.map(i => (
            <ItemCard key={i.id} item={i} personas={personas} onOpen={onOpen} />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loading}
              className="self-center my-2 text-xs text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
